import { Spinner } from "components/shared/Spinner";
import { useState } from "react";
import { autofillCompany, eraseData, migrationCompany, recomputeStocks, syncTCPD } from "store/adminPanelSlice";
import { useAppDispatch, useAppSelector } from "store/store";

import { unclosePreviousPeriod } from "../../../store/periodStore";

const DangerousSettings = () => {
    const [isLoading, setIsLoading] = useState(false);
    const dispatch = useAppDispatch();
    const syncTCPDStatus = useAppSelector((state) => state.adminPanel.syncTCPDStatus);

    const handleRunMigrations = async (version: string) => {
        const confirmation = window.confirm(
            "Are you sure you want to perform this request? Performance will be slower than usual during update.",
        );
        if (confirmation) {
            setIsLoading(true);
            dispatch(migrationCompany({ version })).then(() => setIsLoading(false));
        }
    };

    const handleEraseData = async (type: string) => {
        if (!type) return;
        const confirmation = window.confirm(`Are you sure you want to erase all the ${type} data?`);
        if (confirmation) {
            setIsLoading(true);
            dispatch(eraseData({ type })).then(() => setIsLoading(false));
        }
    };

    const handleAutofill = async (type: "simple" | "inter-title-inter-channel" = "simple") => {
        const confirmation = window.confirm(
            `Are you sure you want to autofill the company?\n A fresh, completely empty company is required.`,
        );
        if (confirmation) {
            setIsLoading(true);
            await dispatch(autofillCompany(type));
            setIsLoading(false);
        }
    };

    const handleSyncTCPD = async () => {
        const confirmation = window.confirm(
            "Are you sure you want to sync title_channel_period_data (TCPD) table with title_channel_sales table for active/open periods?",
        );
        if (confirmation) {
            setIsLoading(true);
            await dispatch(syncTCPD());
            setIsLoading(false);
        }
    };

    const handleRecomputeStocks = async () => {
        const confirmation = window.confirm(
            "Are you sure you want to recompute stocks (netQuantities, returnsQuantities) on title_period_data for all active/open periods of this company?",
        );
        if (confirmation) {
            setIsLoading(true);
            await dispatch(recomputeStocks());
            setIsLoading(false);
        }
    };

    const currentPeriodType = useAppSelector((state) => state.period.currentPeriod?.periodTypeId);
    const previousPeriod = useAppSelector((state) =>
        state.period.periods.find((period) => period.isActive === 0 && period.periodTypeId === currentPeriodType),
    );
    const handleUnclosePreviousPeriod = async () => {
        if (!previousPeriod || isLoading) {
            return;
        }
        const confirmation = window.confirm(
            `Are you sure you want to reopen the period #${previousPeriod.id} ${previousPeriod.periodStart} -> ${previousPeriod.periodEnd} ? This will set the period as active and move all its sales to it.`,
        );
        if (confirmation) {
            setIsLoading(true);
            await dispatch(unclosePreviousPeriod(previousPeriod.id));
            setIsLoading(false);
        }
    };

    return (
        <>
            {isLoading && <Spinner />}

            {!isLoading && (
                <div className="danger-settings-container">
                    <button className="" onClick={handleUnclosePreviousPeriod}>
                        Unclose previous period
                    </button>
                    <hr />

                    {syncTCPDStatus !== "pending" && (
                        <button className="not-danger" onClick={handleSyncTCPD}>
                            Sync TCPD with sales (on active/open periods)
                        </button>
                    )}
                    <hr />

                    <button className="not-danger" onClick={handleRecomputeStocks}>
                        Recompute stocks (on active/open periods)
                    </button>
                    <hr />

                    <div className="danger-settings-buttons">
                        <h3 className="font-bold text-xl">
                            ⚠️ These actions have not been updated recently : do not use them
                        </h3>

                        <div className="text-sm flex flex-col">
                            <span>If you plan to erase data, please ensure to not leave any artifact.</span>
                            <span>
                                Example: Want to delete beneficiaries? Delete terms first. Want to delete titles? Delete
                                sales and terms first.
                            </span>
                            <span>⚠️ Dangerous settings work for companies with **only one period**.</span>
                        </div>

                        <button className="not-danger" onClick={() => handleAutofill("simple")}>
                            Autofill company (Simple)
                        </button>

                        <button className="not-danger mb" onClick={() => handleAutofill("inter-title-inter-channel")}>
                            Autofill company (InterTitle x InterChannel)
                        </button>

                        <button onClick={() => handleEraseData("company")}>Erase current company</button>

                        <button onClick={() => handleEraseData("sales")}>Delete current company sales</button>

                        <button onClick={() => handleEraseData("terms")}>Delete current company terms</button>

                        <button onClick={() => handleEraseData("beneficiaries")}>
                            Delete current company beneficiaries
                        </button>
                        <button onClick={() => handleEraseData("titles")}>Delete current company titles</button>

                        <button onClick={() => handleEraseData("articles")}>Delete current company articles</button>

                        <button onClick={() => handleEraseData("advance")}>
                            Delete current company advance payments (aka a-valoir)
                        </button>

                        {/* {
						!isLoading &&
							<button onClick={() => handleRunMigrations('0.1.6')}>
								ALL: Run 0.1.6 migrations (IBAN validation)
							</button>
					} */}
                        {/* {
						!isLoading &&
							<button onClick={() => handleRunMigrations('0.1.7')}>
								ONCE/EVERYWHERE: Run 0.1.7 migrations (recalculate terms)
							</button>
					} */}
                        <hr />

                        <button onClick={() => handleRunMigrations("0.1.7stock")}>
                            PER COMPANY: Run 0.1.7 stock migrations (recalculate & ignore channels)
                        </button>

                        <button onClick={() => handleRunMigrations("0.1.8")}>
                            ONCE/EVERYWHERE: Run 0.1.8 migrations (copy user accounts on our side)
                        </button>

                        <hr />
                    </div>
                </div>
            )}
        </>
    );
};
export default DangerousSettings;
